import React from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Globe } from '@phosphor-icons/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

interface LocaleSelectorProps {
  className?: string
  variant?: 'button' | 'inline'
}

/**
 * Locale selector component
 * Allows users to switch between supported locales without page reload
 */
export function LocaleSelector({ className, variant = 'button' }: LocaleSelectorProps) {
  const { locale } = useParams<{ locale: string }>()
  const currentLocale = locale || 'en'
  const navigate = useNavigate()
  const location = useLocation()
  const supportedLocales = ['en', 'pt', 'es']

  const localeLabels: Record<string, { label: string; flag: string }> = {
    en: { label: 'English', flag: '🇺🇸' },
    pt: { label: 'Português', flag: '🇧🇷' },
    es: { label: 'Español', flag: '🇪🇸' },
  }

  const handleLocaleChange = (newLocale: string) => {
    // Replace locale in URL without reloading page
    const pathWithoutLocale = location.pathname.replace(/^\/(en|pt|es)/, '')
    navigate(`/${newLocale}${pathWithoutLocale}`)
  }

  if (variant === 'inline') {
    return (
      <div className={`flex gap-1 ${className || ''}`}>
        {supportedLocales.map(loc => (
          <button
            key={loc}
            onClick={() => handleLocaleChange(loc)}
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              currentLocale === loc
                ? 'bg-[var(--pd-primary-bg)] text-[var(--pd-primary-text)]'
                : 'text-[var(--pd-foreground)]/60 hover:text-[var(--pd-foreground)]'
            }`}
            title={localeLabels[loc].label}
          >
            {localeLabels[loc].flag}
          </button>
        ))}
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 rounded-xl hover:bg-[var(--pd-muted)] transition-colors"
          title="Change language"
        >
          <Globe size={20} className="text-[var(--pd-primary-600)]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-40 bg-[var(--pd-card)] border-[var(--pd-border)] rounded-xl shadow-[var(--pd-shadow)]"
        align="end"
        sideOffset={8}
      >
        {supportedLocales.map(loc => (
          <DropdownMenuItem
            key={loc}
            onClick={() => handleLocaleChange(loc)}
            className={`gap-2 px-3 py-2 rounded-lg cursor-pointer ${
              currentLocale === loc
                ? 'bg-[var(--pd-primary-bg)] text-[var(--pd-primary-text)]'
                : ''
            }`}
          >
            <span className="text-lg">{localeLabels[loc].flag}</span>
            <span className="text-sm">{localeLabels[loc].label}</span>
            {currentLocale === loc && (
              <span className="ml-auto text-xs">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
